import { useMutation, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { lendingLoanCreateLoanMutation, lendingLoanListLoansQueryKey } from '@column/api';
import { Icon } from '@column/column-ui-kit';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  Button,
  DatePicker,
  Field,
  FieldGroup,
  FieldLabel,
  Input,
  MoneyInput,
  RadioGroup,
  RadioGroupItem,
  Switch,
  formatAmount,
} from '@column/ui';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { FormField, RenderFields, RestrictedBanner } from '~/components';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { getErrorMessage, loanDropdownBaseQueryKey } from '~/lib/api';
import { BankAccountSelector, EntitySelector } from '~/lib/components/selectors';
import { useNavigate } from '~/lib/navigation';
import { LoanType } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { getDateFormat } from '~/util';

// Loans can only be created in USD.
const LOAN_CURRENCY = 'USD';

type CreateLoanForm = {
  description: string;
  type: LoanType;
  collateralBankAccountId: string;
  isRevolving: boolean;
  entityId: string;
  maturityDate: string;
  maxPrincipalBalance?: number;
};

const LOAN_TYPES: { value: LoanType; label: string }[] = [
  { value: 'unsecured', label: 'Unsecured' },
  { value: 'secured', label: 'Secured' },
];

// The API doesn't check collateral at creation; each disbursement is declined once it exceeds the available balance.
const collateralShortfallMessage = (availableAmount: number, currency: string) =>
  availableAmount > 0
    ? `Balance is below the max principal. Disbursements over ${formatAmount(availableAmount, currency)} will be declined.`
    : 'This account has no available balance. Disbursements will be declined until it is funded.';

const parseDate = (value: string): Date | undefined => {
  const [year, month, day] = value.split('-').map(Number);
  return year && month && day ? new Date(year, month - 1, day) : undefined;
};

export const PageLoansCreate: React.FC = () => {
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { handleSubmitErrors } = useFormErrorHandler();
  const canWrite = useSessionStore((state) => state.isAuthorized({ permission: 'loans', permissionLevel: 'write' }));

  const formMethods = useForm<CreateLoanForm>({
    defaultValues: {
      description: '',
      type: 'unsecured',
      collateralBankAccountId: '',
      isRevolving: false,
      entityId: '',
      maturityDate: '',
    },
  });

  const { handleSubmit, watch } = formMethods;
  const isSecured = watch('type') === 'secured';
  const maxPrincipalBalance = watch('maxPrincipalBalance');

  const createLoan = useMutation({
    ...lendingLoanCreateLoanMutation(),
    onSuccess: (loan) => {
      queryClient.invalidateQueries({ queryKey: lendingLoanListLoansQueryKey() });
      queryClient.invalidateQueries({ queryKey: loanDropdownBaseQueryKey });
      navigate(ROUTE.LOANS);
      addSuccessNotification({ content: `${loan.description} added` });
    },
    onError: (error) => addDangerNotification({ content: getErrorMessage(error) }),
  });

  const handleCreate = (data: CreateLoanForm) =>
    createLoan.mutate({
      body: {
        description: data.description,
        is_revolving: data.isRevolving,
        maturity_date: data.maturityDate,
        entity_id: data.entityId,
        max_principal_balance: data.maxPrincipalBalance ?? 0,
        currency: LOAN_CURRENCY,
        type: data.type,
        collateral_bank_account_id: data.type === 'secured' ? data.collateralBankAccountId : undefined,
      },
    });

  const cancelButton = (
    <Button onClick={() => navigate(ROUTE.LOANS)} variant="secondary" size="sm" type="button">
      Cancel
    </Button>
  );
  const createButton = (
    <Button
      onClick={handleSubmit(handleCreate, handleSubmitErrors)}
      disabled={!canWrite || createLoan.isPending}
      size="sm"
      type="button"
    >
      <Icon.Plus />
      {createLoan.isPending ? 'Creating…' : 'Create Loan'}
    </Button>
  );

  return (
    <>
      <PageActions>
        {cancelButton}
        {createButton}
      </PageActions>

      {!canWrite && (
        <div className="px-(--page-margin) pt-6">
          <RestrictedBanner />
        </div>
      )}

      <FormProvider {...formMethods}>
        <form autoComplete="off" onSubmit={handleSubmit(handleCreate, handleSubmitErrors)}>
          {RenderFields<CreateLoanForm>({
            sections: [
              {
                headline: 'Information',
                fields: [
                  {
                    id: 'description',
                    label: 'Description',
                    rules: { required: true },
                    children: ({ value, onChange, onBlur, ref }, { error }) => (
                      <Input
                        ref={ref}
                        placeholder="Description"
                        aria-invalid={!!error}
                        value={String(value ?? '')}
                        onChange={onChange}
                        onBlur={onBlur}
                      />
                    ),
                  },
                  {
                    content: (
                      <>
                        <FormField<CreateLoanForm> id="type" label="Type" rules={{ required: true }}>
                          {({ value, onChange }) => (
                            <RadioGroup value={String(value ?? '')} onValueChange={onChange}>
                              <FieldGroup variant="outline" orientation="responsive">
                                {LOAN_TYPES.map((loanType) => (
                                  <Field key={loanType.value} orientation="horizontal">
                                    <RadioGroupItem value={loanType.value} id={`loan-type-${loanType.value}`} />
                                    <FieldLabel htmlFor={`loan-type-${loanType.value}`}>{loanType.label}</FieldLabel>
                                  </Field>
                                ))}
                              </FieldGroup>
                            </RadioGroup>
                          )}
                        </FormField>
                        <Accordion type="single" value={isSecured ? 'collateral' : ''}>
                          {/* Widened so the accordion's clipping doesn't crop the selector's focus ring. */}
                          <AccordionItem value="collateral" className="-mx-1 -mb-1 border-b-0">
                            <AccordionContent className="px-1 pt-6 pb-1">
                              <FormField<CreateLoanForm>
                                id="collateralBankAccountId"
                                label="Collateral Account"
                                rules={{ required: isSecured }}
                              >
                                {({ value, onChange, onBlur }, { error }) => (
                                  <BankAccountSelector
                                    value={String(value ?? '')}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    hasError={!!error}
                                    amount={maxPrincipalBalance}
                                    amountCurrency={LOAN_CURRENCY}
                                    isSource
                                    insufficientMessage={collateralShortfallMessage}
                                    insufficientTone="warning"
                                  />
                                )}
                              </FormField>
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>
                      </>
                    ),
                  },
                  {
                    id: 'isRevolving',
                    label: 'Revolving',
                    description: 'Revolving loans can have multiple disbursements.',
                    children: ({ value, onChange }) => (
                      <Switch className="@tablet/form-field:mt-3" checked={Boolean(value)} onCheckedChange={onChange} />
                    ),
                  },
                  {
                    id: 'entityId',
                    label: 'Entity',
                    rules: { required: true },
                    children: ({ value, onChange, onBlur }, { error }) => (
                      <EntitySelector
                        value={String(value ?? '')}
                        onChange={onChange}
                        onBlur={onBlur}
                        hasError={!!error}
                      />
                    ),
                  },
                  {
                    id: 'maturityDate',
                    label: 'Maturity Date',
                    rules: { required: true },
                    children: ({ value, onChange, onBlur }, { error }) => (
                      <DatePicker
                        value={value ? parseDate(String(value)) : undefined}
                        onValueChange={(next) => onChange(next ? getDateFormat(next) : '')}
                        onOpenChange={(open) => !open && onBlur()}
                        aria-invalid={!!error}
                      />
                    ),
                  },
                  {
                    id: 'maxPrincipalBalance',
                    label: 'Max Principal Balance',
                    rules: {
                      required: true,
                      validate: (v) => Number(v) > 0 || 'Max Principal Balance must be greater than zero',
                    },
                    children: ({ value, onChange, onBlur }, { error }) => (
                      <MoneyInput
                        currency={LOAN_CURRENCY}
                        value={typeof value === 'number' ? value : undefined}
                        onValueChange={onChange}
                        onBlur={onBlur}
                        aria-invalid={!!error}
                      />
                    ),
                  },
                ],
              },
            ],
          })}
        </form>
      </FormProvider>

      <div className="flex items-start justify-between px-(--page-margin) py-6">
        {cancelButton}
        {createButton}
      </div>
    </>
  );
};
